import * as React from 'react';

import { IBasicStyledProps } from '@notino/react-styleguide';

import { HorizontalChevron } from '@sharedComponents/Icons/HorizontalChevron';

import {
  DropdownContainer,
  MobileNativeSelect,
  Option,
  OptionsList,
  Selected,
} from './styled';
import { useDropdownA11y } from './useDropdownA11y';

interface DropdownProps<T> extends IBasicStyledProps {
  options: T[];
  selectedKey: string | number;
  onSelect?: (value: T) => void;
  renderSelected: (value: T | undefined, isOpen: boolean) => React.ReactNode;
  renderOption: (value: T, isSelected: boolean) => React.ReactNode;
  mobileNativeSelectConfig?: {
    getLabel: (value: T) => string;
  };
}

function Dropdown<T extends { key: string }>({
  options,
  selectedKey,
  onSelect,
  renderSelected,
  renderOption,
  mobileNativeSelectConfig,
  ...basicStyleProps
}: DropdownProps<T>) {
  const {
    isOpen,
    activeIndex,
    listboxId,
    dropdownRef,
    triggerRef,
    selectedOption,
    toggleDropdown,
    selectOption,
    onTriggerKeyDown,
    onOptionKeyDown,
    setOptionRef,
  } = useDropdownA11y<T>({
    options,
    selectedKey,
    onSelect,
  });

  return (
    <DropdownContainer {...basicStyleProps} ref={dropdownRef}>
      <Selected
        ref={triggerRef}
        onClick={toggleDropdown}
        onKeyDown={onTriggerKeyDown}
        data-id="selected-value"
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        tabIndex={0}
      >
        {renderSelected(selectedOption, isOpen)}
        <HorizontalChevron
          isOpened={isOpen}
          width="1rem"
          height="1rem"
          color="icon.tertiary"
        />
      </Selected>

      {isOpen && (
        <OptionsList data-id="options-list" id={listboxId} role="listbox">
          {options.map((option, index) => (
            <Option
              key={option.key}
              ref={(node) => {
                setOptionRef(index, node);
              }}
              role="option"
              aria-selected={option.key === selectedKey}
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={() => {
                selectOption(option);
              }}
              onKeyDown={(event) => {
                onOptionKeyDown(event, index);
              }}
            >
              {renderOption(option, option.key === selectedKey)}
            </Option>
          ))}
        </OptionsList>
      )}

      {mobileNativeSelectConfig && (
        <MobileNativeSelect
          onChange={(e) => {
            const selected = options.find(
              (option) => option.key === e.target.value
            );
            if (selected !== undefined && onSelect) {
              onSelect(selected);
            }
          }}
          value={selectedKey}
        >
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {mobileNativeSelectConfig.getLabel(option)}
            </option>
          ))}
        </MobileNativeSelect>
      )}
    </DropdownContainer>
  );
}

export default Dropdown;
